import clsx from 'clsx';
import type { ForwardedRef, JSX, RefAttributes } from 'react';
import { getResponsiveVariables, type Responsive, type Spacing } from './components/spacing/types.js';
import type { CommonProps } from './components/types.js';
import { forwardRef } from './forward-ref.js';

export interface StyleProps {
  /** The amount of margin applied to all edges of this component. */
  margin?: Responsive<Spacing | 'auto'>;

  /** The amount of margin applied to the left and right edges of this component. Takes priority over `margin`. */
  marginX?: Responsive<Spacing | 'auto'>;
  /** The amount of margin applied to the top and bottom edges of this component. Takes priority over `margin`. */
  marginY?: Responsive<Spacing | 'auto'>;

  /** The amount of margin applied to the top edge of this component. Takes priority over `marginY` and `margin`. */
  marginTop?: Responsive<Spacing | 'auto'>;
  /** The amount of margin applied to the right edge of this component. Takes priority over `marginX` and `margin`. */
  marginRight?: Responsive<Spacing | 'auto'>;
  /** The amount of margin applied to the bottom edge of this component. Takes priority over `marginY` and `margin`. */
  marginBottom?: Responsive<Spacing | 'auto'>;
  /** The amount of margin applied to the left edge of this component. Takes priority over `marginX` and `margin`. */
  marginLeft?: Responsive<Spacing | 'auto'>;
}

export function withStyleProps<T extends Omit<CommonProps, 'id'>, R>(
  WrappedComponent: (props: T & RefAttributes<R>) => JSX.Element | null,
  displayName?: string,
) {
  const ComponentWithStyleProps = (
    {
      margin: responsiveMargin,
      marginX: responsiveMarginX,
      marginY: responsiveMarginY,
      marginTop: responsiveMarginTop,
      marginRight: responsiveMarginRight,
      marginBottom: responsiveMarginBottom,
      marginLeft: responsiveMarginLeft,
      UNSAFE_className,
      UNSAFE_style,
      ...props
    }: T & StyleProps,
    ref: ForwardedRef<R>,
  ) => {
    const marginTop = getResponsiveVariables(
      'box-mt',
      'space',
      responsiveMarginTop ?? responsiveMarginY ?? responsiveMargin,
    );

    const marginRight = getResponsiveVariables(
      'box-mr',
      'space',
      responsiveMarginRight ?? responsiveMarginX ?? responsiveMargin,
    );

    const marginBottom = getResponsiveVariables(
      'box-mb',
      'space',
      responsiveMarginBottom ?? responsiveMarginY ?? responsiveMargin,
    );

    const marginLeft = getResponsiveVariables(
      'box-ml',
      'space',
      responsiveMarginLeft ?? responsiveMarginX ?? responsiveMargin,
    );

    return (
      <WrappedComponent
        {...(props as T)}
        ref={ref}
        UNSAFE_className={clsx(UNSAFE_className, {
          'cim-box': marginTop || marginRight || marginBottom || marginLeft,
        })}
        UNSAFE_style={{
          ...UNSAFE_style,
          ...marginTop,
          ...marginRight,
          ...marginBottom,
          ...marginLeft,
        }}
      />
    );
  };

  // @ts-expect-error missing type definition for displayName
  const existingDisplayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  return forwardRef(ComponentWithStyleProps, displayName || `WithStyleProps(${existingDisplayName})`);
}
